import { useInterval } from '@/composables/useInterval';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { loader } from '@/utils/signals/loader';
import { Component, computed, EventEmitter, inject, input, signal } from '@angular/core';
import { BuildingScanImportDto, BuildingScanItemDto, BuildingScanService } from '@artemis-software/wr-api';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { PendingChanges } from '@guards/pending-changes.guard';
import { FileUploadService } from '@services/file-upload.service';
import { NbDialogService } from '@nebular/theme';
import { BuildingScanDialogComponent } from '@components/dialog/building-scan-dialog/building-scan-dialog.component';
import { ScanSectionSelectDialogComponent } from '@components/dialog/scan-section-select-dialog/scan-section-select-dialog.component';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
const ScanType = BuildingScanItemDto.TypeEnum;

@Component({
  standalone: false,
  selector: 'wr-building-scan-list',
  templateUrl: './building-scan-list.component.html',
  styleUrls: ['./building-scan-list.component.scss'],
})
export class BuildingScanListComponent implements PendingChanges {

  buildingId = input.required<string>();

  editMode = input<boolean>(false);

  searchText = input<string | undefined>(undefined);

  readonly fileUploadService = inject(FileUploadService);
  readonly buildingScanService = inject(BuildingScanService);
  readonly dialogService = inject(NbDialogService);

  readonly progress = signal(0);

  readonly uploading = signal(false);

  readonly loading = loader();

  constructor() {
    useInterval(() => this.reload(), 10_000);
  }

  hasPendingChanges(): boolean | Promise<boolean> {
    return this.uploading();
  }

  readonly scans = asyncComputed(
    () => (this.buildingId() ? this.buildingScanService.getBuildingScans(this.buildingId()) : []),
    [],
    this.loading,
  );

  readonly filteredScans = computed(() => {
    const allScans = this.scans();
    const query = this.searchText()?.toLowerCase().trim();

    if (!query) {
      return allScans;
    }

    return allScans.filter(scan =>
      scan.name?.toLowerCase().includes(query)
    );
  });

  readonly pendingScans = asyncComputed(
    () => (this.buildingId() ? this.buildingScanService.getImportStatus(this.buildingId()) : []),
    [],
    this.loading,
  );

  readonly empty = computed(() => this.scans().length === 0 && this.pendingScans().length === 0);

  private pendingUpload?: AbortController;

  reload() {
    this.scans.reload();
    this.pendingScans.reload();
  }

  async onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';

    if (!file) {
      return;
    }

    const sectionIds: string[] | undefined | false = await firstValueFrom(this.dialogService.open(ScanSectionSelectDialogComponent, {
      context: {
        buildingId: this.buildingId(),
      },
      closeOnBackdropClick: false,
      closeOnEsc: true,
      autoFocus: true,
    }).onClose);

    if (!sectionIds || sectionIds.length === 0) {
      return;
    }

    const fileUploaded = new Subject();
    const progressEmitter = new EventEmitter<number>();

    progressEmitter.pipe(takeUntil(fileUploaded)).subscribe((progress) => {
      this.progress.set(progress);
    });

    try {
      const abortController = this.pendingUpload = new AbortController();

      this.uploading.set(true);
      const preparation = await firstValueFrom(this.buildingScanService.prepareImport({
        buildingId: this.buildingId(),
        type: ScanType.Panorama,
        fileSize: file.size,
        sectionIds,
      }));
      const parts = await this.fileUploadService.uploadFileParts(file, preparation.partSize, preparation.presignedUrls, progressEmitter, abortController.signal);
      await firstValueFrom(this.buildingScanService.importScan({ importId: preparation.importId, parts }));
      this.reload();
    } finally {
      this.uploading.set(false);
      fileUploaded.next(undefined);
      this.progress.set(0);
      this.pendingUpload = undefined;
    }
  }

  onScanUpdated(scan: BuildingScanItemDto) {
    const index = this.scans().findIndex(s => s.id === scan.id);
    if (index >= 0) {
      const scans = [...this.scans()];
      scans[index] = scan;
      this.scans.set(scans);
    } else {
      this.scans.set([...this.scans(), scan]);
    }
  }

  linkPointCloud() {
    this.dialogService.open(BuildingScanDialogComponent, {
      context: {
        scanId: undefined,
        name: undefined,
        pointCloudId: undefined,
        buildingId: this.buildingId(),
        type: ScanType.PointCloud,
      },
      closeOnBackdropClick: false,
      closeOnEsc: true,
      autoFocus: true,
    }).onClose.subscribe(() => {
      this.reload();
    });
  }

  editScan(scan: BuildingScanItemDto) {
    this.dialogService.open(BuildingScanDialogComponent, {
      context: {
        scanId: scan.id,
        name: scan.name,
        pointCloudId: scan.pointCloudId,
        buildingId: this.buildingId(),
        sectionIds: scan.sections?.map(section => section.id),
        type: scan.type,
      },
      closeOnBackdropClick: false,
      closeOnEsc: true,
      autoFocus: true,
    }).onClose.subscribe((buildingScan?: BuildingScanItemDto | false) => {
      if (buildingScan) {
        this.onScanUpdated(buildingScan);
      }
    });
  }

  abortUpload() {
    this.pendingUpload?.abort();
  }

  async deleteBuildingScan(buildingScanItem: BuildingScanItemDto) {
    if (await firstValueFrom(this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Gebäudescan löschen',
        message: 'Bist du sicher, dass du diesen Gebäudescan löschen willst?',
      },
    }).onClose)) {
      await firstValueFrom(this.buildingScanService.deleteBuildingScan(buildingScanItem.id));
      this.reload();
    }
  }

  async deleteScanImport(scanImport: BuildingScanImportDto) {
    if (await firstValueFrom(this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Scan-Import löschen',
        message: 'Bist du sicher, dass du diesen fehlgeschlagenen Scan-Import löschen willst?',
      },
    }).onClose)) {
      await firstValueFrom(this.buildingScanService.deleteImport(scanImport.id));
      this.reload();
    }
  }
}
